import React, { useState, useEffect } from 'react';
import { PlayCircle, BookOpen, RefreshCw, AlertCircle, Sparkles } from 'lucide-react';
import useUnitCode from '../hooks/useUnitCode';
import UnitCard from '../components/unit/UnitCard';
import { MyUnitCodeDto } from '../types/unit';
import { useNavigate, useLocation } from 'react-router-dom';

const MyLectures: React.FC = () => {
  const { 
    units, 
    isLoading, 
    error, 
    totalCount, 
    totalPages, 
    currentPage,
    getMyUnits,
    clearError,
    refreshUnits
  } = useUnitCode();

  const navigate = useNavigate();
  const location = useLocation();

  const [filteredUnits, setFilteredUnits] = useState<MyUnitCodeDto[]>([]);

  // Force refresh units every time we navigate to this route
  // Using location.key ensures it triggers on every navigation, even to the same route
  useEffect(() => {
    // Clear cache and fetch fresh data on route navigation
    refreshUnits();
  }, [location.key, refreshUnits]);

  // Check for refresh parameter and force refresh if present
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const refreshParam = urlParams.get('refresh');
    
    if (refreshParam) {
      // Force refresh the units data
      refreshUnits();
      // Clean up the URL by removing the refresh parameter
      navigate('/my-lectures', { replace: true });
    }
  }, [location.search, refreshUnits, navigate]);

  // Update filtered units when units change
  useEffect(() => {
    setFilteredUnits(units);
  }, [units]);

  const handlePageChange = (page: number) => {
    getMyUnits(page);
  };

  const handleRefresh = () => {
    clearError();
    refreshUnits();
  };

  const handleUnitClick = (unitId: number) => {
    navigate(`/unit-viewer/${unitId}`);
  };

  // Loading shimmer component
  const UnitCardShimmer = () => (
    <div className="bg-white/80 backdrop-blur-xl rounded-xl border border-white/30 shadow-md overflow-hidden animate-pulse">
      <div className="p-3 bg-gradient-to-br from-gray-100 to-gray-200">
        <div className="w-10 h-10 mx-auto bg-gray-300 rounded-xl mb-2"></div>
      </div>
      <div className="p-3">
        <div className="h-4 bg-gray-300 rounded mb-1.5"></div>
        <div className="h-3 bg-gray-200 rounded mb-3"></div>
        <div className="h-8 bg-gray-300 rounded"></div>
      </div>
    </div>
  );

  // Empty state component
  const EmptyState = () => (
      <div className="text-center py-12">
      <div className="inline-block p-4 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full mb-4">
        <PlayCircle className="w-12 h-12 text-emerald-600" />
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-3">لا توجد محاضرات بعد</h3>
      <p className="text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
        لم تقم بإدخال أي أكواد محاضرات حتى الآن. قم بإدخال كود المحاضرة للوصول إلى المحتوى التعليمي.
      </p>
    </div>
  );

  // Error state component
  const ErrorState = () => (
      <div className="text-center py-12">
      {/* <div className="inline-block p-6 bg-red-100 rounded-full mb-6">
        <AlertCircle className="w-16 h-16 text-red-600" />
      </div> */}
      <h3 className="text-lg font-bold text-gray-900 mb-3">أنت ليس لديك محاضرات</h3>
      <p className="text-sm text-gray-600 mb-4">{error}</p>
      <button
        onClick={handleRefresh}
        className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg text-sm font-medium shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
      >
        <RefreshCw className="w-3 h-3" />
        إعادة المحاولة
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-white pt-20" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-100 to-teal-100 px-4 py-2 rounded-full border border-emerald-200/50 mb-4">
            <PlayCircle className="w-4 h-4 text-emerald-600" />
            <span className="text-emerald-700 font-medium text-sm">محاضراتي</span>
          </div>
          
          <h1 className="text-2xl lg:text-3xl font-black mb-4 leading-tight">
            <span className="inline-flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-lg">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-gray-800">محاضرات السنتر</span>
              <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent"></span>
            </span>
          </h1>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          </p>
        </div>

        {/* Stats */}
        {!isLoading && !error && (
          <div className="text-center mb-6">
            <p className="text-gray-600 text-sm">
              {filteredUnits.length > 0 ? (
                <>
                  عرض <span className="font-semibold text-emerald-600">{filteredUnits.length}</span> من أصل{' '}
                  <span className="font-semibold text-emerald-600">{totalCount}</span> وحدة
                </>
              ) : (
                'لا توجد وحدات متاحة'
              )}
            </p>
          </div>
        )}

        {/* Content */}
        {error ? (
          <ErrorState />
        ) : isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <UnitCardShimmer key={`shimmer-${index}`} />
            ))}
          </div>
        ) : filteredUnits.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredUnits.map((unit) => (
              <div
                key={unit.unitId}
                onClick={() => handleUnitClick(unit.unitId)}
                className="cursor-pointer"
              >
                <UnitCard unit={unit} />
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && !error && totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-8">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm rounded-lg bg-white text-gray-600 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 border border-gray-200"
            >
              السابق
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-all duration-200 ${
                    currentPage === page
                      ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg'
                      : 'bg-white text-gray-600 hover:bg-emerald-50 hover:text-emerald-600 border border-gray-200'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-sm rounded-lg bg-white text-gray-600 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 border border-gray-200"
            >
              التالي
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyLectures; 